import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import './Settings.css';
import { API_BASE_URL } from '../../api';

const Settings = () => {
  const [settings, setSettings] = useState({
    profile: {
      business_name: '',
      ownerName: '',
      email: '',
      phone: '',
      shopAddress: '',
      logo_url: '',
      banner_url: '',
      statusLabel: ''
    }
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const getVendorToken = () => {
    let token = localStorage.getItem('token');
    if (!token) {
      try {
        const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
        token = v?.token || '';
      } catch (_) {}
    }
    console.log('Token found:', !!token, token ? `${token.substring(0, 20)}...` : 'none');
    return token;
  };

  const getVendorDataFromStorage = () => {
    try {
      const vendorData = JSON.parse(localStorage.getItem('vendorData') || 'null');
      console.log('Vendor data from localStorage:', vendorData);
      return vendorData;
    } catch (e) {
      console.error('Error parsing vendor data:', e);
      return null;
    }
  };

  const getVendorId = () => {
    const vendorData = getVendorDataFromStorage();
    return vendorData?.id || vendorData?.vendor_id || null;
  };

  const handleUpload = async (type, file) => {
    if (!file) return;
    try {
      const token = getVendorToken();
      const form = new FormData();
      if (type === 'logo') form.append('logo', file);
      if (type === 'banner') form.append('banner', file);
      const res = await fetch(`${API_BASE_URL}/api/upload/vendor-media`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Upload failed');
      } else {
        setSettings(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            logo_url: data.logo_url !== undefined ? data.logo_url : prev.profile.logo_url,
            banner_url: data.banner_url !== undefined ? data.banner_url : prev.profile.banner_url
          }
        }));
      }
    } catch (e) { console.error('Media upload error:', e); }
  };

  const removeMedia = async (type) => {
    try {
      const token = getVendorToken();
      const res = await fetch(`${API_BASE_URL}/api/upload/vendor-media?type=${encodeURIComponent(type)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Remove failed');
      } else {
        setSettings(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            logo_url: type === 'logo' ? '' : prev.profile.logo_url,
            banner_url: type === 'banner' ? '' : prev.profile.banner_url
          }
        }));
      }
    } catch (e) { console.error('Remove media error:', e); }
  };

  const loadVendorProfile = async () => {
    try {
      const token = getVendorToken();
      const vendorId = getVendorId();
      const localVendorData = getVendorDataFromStorage();
      
      console.log('Loading vendor profile...', { vendorId, hasToken: !!token });
      
      // Set initial data from localStorage if available
      if (localVendorData) {
        setSettings(prev => ({
          ...prev,
          profile: {
            business_name: localVendorData.shopName || localVendorData.business_name || '',
            ownerName: localVendorData.ownerName || localVendorData.owner_name || '',
            email: localVendorData.email || localVendorData.owner_email || '',
            phone: localVendorData.phone || localVendorData.owner_phone || '',
            shopAddress: localVendorData.shopAddress || localVendorData.address || '',
            logo_url: localVendorData.logo_url || '',
            banner_url: localVendorData.banner_url || '',
            statusLabel: localVendorData.status || ''
          }
        }));
      }
      
      if (!vendorId) {
        console.error('No vendor ID found');
        return;
      }
      
      if (!token) {
        console.error('No token found');
        return;
      }
      
      // Fetch fresh data from API
      const res = await fetch(`${API_BASE_URL}/api/vendors/${vendorId}`, { 
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('API Response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('Vendor profile data received:', data);
        
        // Update with fresh API data
        setSettings(prev => ({
          ...prev,
          profile: {
            business_name: data.business_name || '',
            ownerName: (data.owner_name === null || data.owner_name === undefined) ? '' : data.owner_name,
            email: (data.email === null || data.email === undefined) ? (data.owner_email || '') : data.email,
            phone: (data.phone === null || data.phone === undefined) ? (data.owner_phone || '') : data.phone,
            shopAddress: (data.shopAddress === null || data.shopAddress === undefined) ? (data.address || '') : data.shopAddress,
            logo_url: data.logo_url || '',
            banner_url: data.banner_url || '',
            statusLabel: data.status || ''
          }
        }));
        
        console.log('Settings updated with fresh API data');
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Failed to load vendor profile:', res.status, errorData);
        console.log('Using localStorage data as fallback');
      }
    } catch (e) { 
      console.error('Load profile error:', e);
      console.log('Using localStorage data as fallback');
    }
  };

  useEffect(() => { loadVendorProfile(); }, []);

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple saves
    
    setIsSaving(true);
    try {
      const token = getVendorToken();
      
      if (!token) {
        alert('Please log in to save changes');
        return;
      }
      
      // Save basic profile information
      const vendorId = getVendorId();
      if (!vendorId) {
        alert('Vendor ID not found. Please log in again.');
        return;
      }
      
      const requestData = {
        business_name: settings.profile.business_name,
        owner_name: settings.profile.ownerName,
        owner_email: settings.profile.email,
        owner_phone: settings.profile.phone,
        address: settings.profile.shopAddress
      };
      
      console.log('🚀 Saving vendor profile...');
      console.log('Vendor ID:', vendorId);
      console.log('Request Data:', requestData);
      console.log('Token present:', !!token);
      
      const profileRes = await fetch(`${API_BASE_URL}/api/vendors/${vendorId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      console.log('Response Status:', profileRes.status);
      console.log('Response Headers:', Object.fromEntries(profileRes.headers.entries()));
      
      if (!profileRes.ok) {
        const errorText = await profileRes.text();
        console.error('❌ Profile save failed:');
        console.error('Status:', profileRes.status);
        console.error('Status Text:', profileRes.statusText);
        console.error('Response Body:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          console.error('Failed to parse error response as JSON:', parseError);
          errorData = { error: errorText || 'Failed to save profile' };
        }
        
        console.error('Parsed Error Data:', errorData);
        alert(`Failed to save profile: ${errorData.error || errorData.message || 'Unknown error'}`);
        return;
      }
      
      const responseData = await profileRes.json();
      console.log('✅ Profile saved successfully:', responseData);
      
      // Reload profile data to show updated information
      await loadVendorProfile();
      
      // Show success message
      alert('All changes saved successfully!');
      
    } catch (e) { 
      console.error('💥 Save profile error:', e);
      console.error('Error stack:', e.stack);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };


  const renderProfileSettings = () => (
    <div className="settings-section">
      <div className="shop-summary-card">
        <div className="shop-summary-content">
          {settings.profile.logo_url ? (
            <img src={settings.profile.logo_url} alt="logo" className="shop-logo" />
          ) : (
            <div className="shop-logo-placeholder">
              {settings.profile.business_name ? settings.profile.business_name.charAt(0).toUpperCase() : 'S'}
            </div>
          )}
          <div className="shop-summary-info">
            <h2 className="shop-name">{settings.profile.business_name || '—'}</h2>
            <div className="shop-details-row">
              <span className="shop-detail-item">{settings.profile.shopAddress || '—'}</span>
            </div>
            <div className="shop-details-row">
              <span className="shop-detail-item">{settings.profile.email || '—'}</span>
              {settings.profile.phone && <span className="shop-detail-separator">•</span>}
              {settings.profile.phone && <span className="shop-detail-item">{settings.profile.phone}</span>}
            </div>
          </div>
          {settings.profile.statusLabel && (
            <div className="shop-status-badge">
              {settings.profile.statusLabel}
            </div>
          )}
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="form-section">
        <h3 className="section-title">Business & Owner Information</h3>
        <p className="section-description">Update your business details and owner information</p>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Business Name</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.business_name}
            onChange={(e) => handleSettingChange('profile', 'business_name', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Owner Name</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.ownerName}
            onChange={(e) => handleSettingChange('profile', 'ownerName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-input"
            value={settings.profile.email}
            onChange={(e) => handleSettingChange('profile', 'email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            className="form-input"
            value={settings.profile.phone}
            onChange={(e) => handleSettingChange('profile', 'phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Shop Address</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.shopAddress}
            onChange={(e) => handleSettingChange('profile', 'shopAddress', e.target.value)}
          />
        </div>
      </div>

      </div>
    </div>
  );

  const renderContent = () => {
    // Always render profile settings for mobile-only view
    return renderProfileSettings();
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <div className="header-content">
          <h1>Profile Settings</h1>
          <p>Manage your business profile and details</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          <Save size={20} />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="settings-container">
        {/* Sidebar removed for mobile-only single tab view */}
        
        <div className="settings-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
